import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useHistory } from 'react-router-dom'
import { isEmpty, toInteger } from 'lodash'
import pluralize from 'pluralize'
import { IconInfoCircle } from '@tabler/icons-react'

import { Loader } from '@/components/loaders/Loader'
import { fetchQaAuditEventsByCallId } from '@/reducers/scorecards/scores.actions'
import { updateScoreCriteria } from '@/reducers/callSearch/callSearch.actions'
import { submitPromptFeedback } from '@/reducers/promptFeedback/promptFeedback.actions'
import NoData from '@/components/NoData'
import { Banner } from '@/components/banners/Banner'
import { CHANNEL_TYPE } from '@/utils/constants'
import {
  passFailToAiResponse,
  aiResponseToScore,
  findCriteriaInConfig,
} from '@/utils/promptFeedback.helpers'

import { setCallExplorerData } from '@/reducers/callSearch/callSearch.redux'
import {
  CALL_EXPLORER_CRITERIA_FILTER_ENUMS as enums,
  parseCallExplorerParams,
  removeCallExplorerParams,
  updateCallExplorerParams,
} from './helpers'
import { QAScore } from './QAScore'
import { QAScoreEditMode } from './QAScoreEditMode'

import './QAScore.scss'

export const CallQAScores = ({
  callId,
  organizationId,
  metadata = {},
  setOpenTab,
  callType = CHANNEL_TYPE.CALL,
}) => {
  const [scorecardIdToEdit, setScorecardIdToEdit] = useState(null)
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()

  const {
    callExplorer: { qaScores },
    loading: { qaScores: qaScoresLoading },
  } = useSelector((state) => state.callSearch)
  const { user_id: userId } = useSelector((state) => state.currentUser)
  const { targetScorecardConfig } = useSelector((state) => state.scorecards.data)
  const skippedScorecards =
    !isEmpty(metadata?.qa_eligibility) &&
    metadata?.qa_eligibility.map((skip) => {
      return { scorecardName: skip.config_name, reason: skip.reason }
    })

  useEffect(() => {
    const { scorecardId } = parseCallExplorerParams(location)

    if (scorecardId) {
      setScorecardIdToEdit(Number(scorecardId))
    }
  }, [])

  // A scorecard note jumps here to land on its scorecard card in the list
  // (not the criteria editor — that's where criteria notes go). Scroll once
  // the scores are rendered, then drop the param so it doesn't re-fire.
  useEffect(() => {
    const { scrollToScorecardId } = parseCallExplorerParams(location)
    if (!scrollToScorecardId || isEmpty(qaScores)) {
      return
    }
    const element = document.getElementById(`qa-scorecard-${scrollToScorecardId}`)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      removeCallExplorerParams(location, history, ['scrollToScorecardId'])
    }
  }, [qaScores])

  const handleEditScore = (score) => {
    const needsManualScoring = ['partially_scored', 'unscored'].includes(score.scored_status)
    const params = { scorecardId: score.id }
    if (needsManualScoring) {
      dispatch(setCallExplorerData({ selectedCriteriaFilter: enums.INCOMPLETE }))
      updateCallExplorerParams(location, history, {
        ...params,
        scoreFilter: enums.INCOMPLETE,
      })
    } else {
      updateCallExplorerParams(location, history, params)
    }

    setScorecardIdToEdit(score.id)
  }

  const handleCancelEdit = () => {
    setScorecardIdToEdit(null)
  }

  // Helper to find criteria score by UUID in the scorecard
  const findCriteriaScoreByUuid = (scorecard, uuid) => {
    for (const sectionScore of scorecard?.section_scores || []) {
      for (const measureScore of sectionScore.measure_scores || []) {
        for (const criteriaScore of measureScore.criteria_scores || []) {
          if (criteriaScore.uuid === uuid) {
            return criteriaScore
          }
        }
      }
    }
    return null
  }

  const handleSaveScore = (formValues, initialValues) => {
    const scorecard = qaScores?.find((score) => score.id === scorecardIdToEdit)

    // Criteria notes are saved independently through /api/notes via the
    // NotesPanel; this payload only carries score changes now. Skip
    // feedback keys (separate submission path below).
    const updatedCriteria = Object.keys(formValues)
      .filter((criteriaUuid) => {
        if (criteriaUuid.includes('-feedback')) {
          return false
        }
        return formValues[criteriaUuid] !== initialValues[criteriaUuid]
      })
      .map((criteriaUuid) => {
        const score = formValues[criteriaUuid]
        const skipped = score === '-1'

        return {
          scorecard_criteria_score_uuid: criteriaUuid,
          score: toInteger(score),
          skipped,
        }
      })

    // Collect all feedback submissions for AI criteria score changes
    const feedbackKeys = Object.keys(formValues).filter((key) => key.includes('-feedback'))

    const feedbackSubmissions = feedbackKeys
      .map((key) => {
        const feedbackText = formValues[key]
        if (isEmpty(feedbackText)) {
          return null
        }

        // Extract the criteria UUID from the key (remove '-feedback' suffix)
        const criteriaUuid = key.replace('-feedback', '')
        const criteriaScore = findCriteriaScoreByUuid(scorecard, criteriaUuid)

        if (!criteriaScore || criteriaScore.criteria_type !== 'ai') {
          return null
        }

        // Find the criteria config to get must_be_present
        const criteriaConfig = findCriteriaInConfig(
          targetScorecardConfig,
          criteriaScore.scorecard_criteria_id
        )
        const mustBePresent = criteriaConfig?.must_be_present ?? true

        // Only submit feedback if the new score differs from the AI's original response
        const aiScore = aiResponseToScore(
          criteriaScore.ai_response,
          mustBePresent,
          criteriaScore.score
        )
        const newScore = toInteger(formValues[criteriaUuid])
        if (newScore === aiScore) {
          return null
        }
        const expectedResult = passFailToAiResponse(
          newScore === 1 ? 'pass' : newScore === 0 ? 'fail' : 'n/a',
          mustBePresent
        )

        return {
          promptId: criteriaConfig?.prompt_id,
          promptName: criteriaScore.name,
          callId,
          scorecardCriteriaScoreId: criteriaScore.id,
          scorecardCriteriaScid: criteriaScore.scid,
          expectedResult,
          feedbackText,
          organizationId,
          organizationName: metadata?.organization_name,
          agentId: userId,
          callType,
        }
      })
      .filter(Boolean)

    // Submit all feedback tasks
    feedbackSubmissions.forEach((feedback) => {
      dispatch(submitPromptFeedback(feedback))
    })

    dispatch(updateScoreCriteria(scorecardIdToEdit, updatedCriteria, scorecard?.organization_id))
    dispatch(fetchQaAuditEventsByCallId(callId, organizationId))
    setScorecardIdToEdit(null)
  }

  const buildSkippedScorecardsTable = () => (
    <Banner
      data-testid="copilot-scorecards-not-eligible"
      informative
      shrunk
      icon={<IconInfoCircle />}
    >
      <p>
        This {callType} was not scored for{' '}
        {`${pluralize('scorecard', Number(skippedScorecards.length), true)}`}. Our system uses
        specific criteria to determine eligibility, and this {callType} did not meet those
        requirements. For more details,{' '}
        <a data-testid="banner-link-to-details" onClick={() => setOpenTab('details')}>
          click here
        </a>
        .
      </p>
    </Banner>
  )

  if (qaScoresLoading) {
    return (
      <div className="empty-table">
        <Loader />
      </div>
    )
  }

  if (!isEmpty(qaScores)) {
    if (scorecardIdToEdit) {
      const score = qaScores.find((score) => score.id === scorecardIdToEdit)

      if (score) {
        return (
          <QAScoreEditMode
            score={score}
            handleCancelEdit={handleCancelEdit}
            handleSaveScore={handleSaveScore}
            callId={callId}
            userId={userId}
            callType={callType}
          />
        )
      }
    }

    return (
      <div className="qa-scores-container">
        {!isEmpty(skippedScorecards) && buildSkippedScorecardsTable()}
        <div className="qa-scores">
          {qaScores.map((scorecard) => (
            <div className="qa-score" key={scorecard.uuid} id={`qa-scorecard-${scorecard.id}`}>
              {/* QAScore owns the scorecard note now (toggled from its header,
                  next to View Score). */}
              <QAScore
                key={scorecard.uuid}
                scorecard={scorecard}
                handleEditScore={handleEditScore}
                totalScorecards={qaScores.length}
                organizationId={organizationId}
                callType={callType}
              />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <>
      {!isEmpty(skippedScorecards) && buildSkippedScorecardsTable()}
      <div className="empty-table">
        <NoData text={`No QA scores associated with this ${callType}`} />
      </div>
    </>
  )
}
